import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { Compass } from 'lucide-react';
import ProductTour from './ProductTour';
import { TOURS, TourId } from './tours';
import '../../styles/product-tour.css';

const seenKey = (id: TourId) => `axon.tour.${id}.v1`;

const hasSeen = (id: TourId) => {
    try { return localStorage.getItem(seenKey(id)) === 'done'; } catch { return true; }
};

/**
 * Drop-in per page: shows the page's tour once on the first visit, adds a small
 * "Tour" button to replay it, and starts it on demand with `?tour=<id>`.
 */
const PageTour: React.FC<{ id: TourId }> = ({ id }) => {
    const tour = TOURS[id];
    const [open, setOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const forced = searchParams.get('tour') === id;

    useEffect(() => {
        if (!forced && hasSeen(id)) return;
        // Give the page a moment to render its first data before pointing at things
        const t = window.setTimeout(() => setOpen(true), forced ? 300 : 1200);
        return () => window.clearTimeout(t);
    }, [id, forced]);

    const close = useCallback((_completed: boolean, shown: boolean = true) => {
        setOpen(false);
        // Only remember the tour once the user has actually seen it
        if (shown) {
            try { localStorage.setItem(seenKey(id), 'done'); } catch { /* storage unavailable */ }
        }
        if (searchParams.get('tour')) {
            const next = new URLSearchParams(searchParams);
            next.delete('tour');
            setSearchParams(next, { replace: true });
        }
    }, [id, searchParams, setSearchParams]);

    return createPortal(
        <>
            <button
                type="button"
                className="ax-tour-fab"
                onClick={() => setOpen(true)}
                aria-label={`Take the tour: ${tour.name}`}
                title={`Quick tour: ${tour.name}`}
            >
                <Compass size={16} aria-hidden="true" />
                <span>Tour</span>
            </button>
            {open && <ProductTour steps={tour.steps} open={open} onClose={close} />}
        </>,
        document.body
    );
};

export default PageTour;
